import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Table, UserAbility, createCustomColumn, createTextColumn, useTableState } from '../deps.js';
import RolesInfoModal from './RolesInfoModal.js';
import AbilitiesInfoModal from './AbilitiesInfoModal.js';

const useAdminRoles = (directory) => {
  const { roles, tableIds } = directory;
  const [rolesInfoModalOpen, setRolesInfoModalOpen] = useState(false);
  const [abilitiesInfoModalOpen, setAbilitiesInfoModalOpen] = useState(false);

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
  } = useTableState(tableIds.roles);

  const columns = useMemo(() => {
    const roleHasAbility = (roleId, ability) => {
      const roleObj = roles[roleId];
      return roleObj && roleObj.abilities && Array.isArray(roleObj.abilities) &&
        roleObj.abilities.includes(ability);
    };

    return [
    createTextColumn(
      'title',
      'Role Title',
      (role) => role.title || 'Unknown'
    ),
    ...Object.keys(UserAbility).map((ability) =>
      createCustomColumn(
        ability,
        UserAbility[ability].displayName,
        (role) => roleHasAbility(role.id, ability),
        (value, role) => (
          roleHasAbility(role.id, ability) ? (
            <FontAwesomeIcon icon={faCheck} className="admin-ability-check" />
          ) : (
            <FontAwesomeIcon icon={faTimes} className="admin-ability-times" />
          )
        ),
        {
          sortable: true,
          filterable: false,
          className: 'admin-ability-column',
        }
      )
    ),
  ];
  }, [roles]);

  const tableData = useMemo(() => (
    Object.entries(roles).map(([roleId, roleData]) => ({
      id: roleId,
      title: roleData.title || roleId,
      ...roleData,
    }))
  ), [roles]);

  const headerActions = (
    <>
      <button
        className="roles-info-button"
        onClick={() => setRolesInfoModalOpen(true)}
        title="What are roles?"
      >
        <FontAwesomeIcon icon={faQuestionCircle} /> What are roles?
      </button>
      <button
        className="abilities-info-button"
        onClick={() => setAbilitiesInfoModalOpen(true)}
        title="What are abilities?"
      >
        <FontAwesomeIcon icon={faQuestionCircle} /> What are abilities?
      </button>
    </>
  );

  const section = (
    <div className="admin-table-section">
      <h2 className="admin-table-title">Role Permissions</h2>
      <Table
        id={tableIds.roles}
        data={tableData}
        columns={columns}
        getRowKey={(role) => role.id}
        sortState={sortState}
        onSortChange={setSortState}
        filterValues={filterValues}
        onFilterChange={setFilterValues}
        disabledFilters={disabledFilters}
        onDisabledFiltersChange={setDisabledFilters}
        paginationState={paginationState}
        onPaginationChange={setPaginationState}
        enableSelection={false}
        emptyMessage="No roles found."
      />
    </div>
  );

  const modals = (
    <>
      <RolesInfoModal
        isOpen={rolesInfoModalOpen}
        onClose={() => setRolesInfoModalOpen(false)}
        roles={roles}
      />
      <AbilitiesInfoModal
        isOpen={abilitiesInfoModalOpen}
        onClose={() => setAbilitiesInfoModalOpen(false)}
      />
    </>
  );

  return { headerActions, section, modals };
};

export default useAdminRoles;
